/**
 * @template T
 * @typedef {Object} TApiResponse
 * @property {boolean} error
 * @property {string} message
 * @property {T|null} data
 * @property {T|null} referenceName |
 */

/**
 * @typedef {Object<string, any>} QueryParams
 * @typedef {Object<string, any>} Headers
 */

import axios from 'axios';
import URLParse from 'url-parse';
import { getApiUrl } from '../utils/Env';

/**
 * Helper to generate URL with optional query parameters
 * @param {string} path
 * @param {QueryParams} [query]
 * @returns {string}
 */
const generateUrl = (path, query = null) => {
    // Helper function to add query parameters to a URL
    const addQueryParams = (fullUrl, queryParams = {}) => {
        const URL = new URLParse(fullUrl, true);
        URL.query = { ...URL.query, ...queryParams };
        return URL.toString();
    };

    let finalUrl;
    // If starts with http or https, use as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
        finalUrl = new URL(path);
    }
    // If starts with /, prepend api url
    else if (path.startsWith('/')) {
        finalUrl = new URL(`${getApiUrl()}${path}`);
    }
    // Otherwise, treat as a complete URL
    else {
        finalUrl = new URL(`${getApiUrl()}/${path}`);
    }

    // Add query parameters if provided
    if (query) {
        finalUrl = addQueryParams(finalUrl, query);
    }

    return finalUrl.toString();
};
// ---------------------------
// Main Hook
// ---------------------------
function useApi() {
    /**
     * @template T
     * @param {"get"|"post"|"put"|"delete"} method
     * @param {string} path
     * @param {any} [payload]
     * @param {QueryParams} [query]
     * @param {Headers} [headers]
     * @returns {Promise<TApiResponse<T>>}
     */
    const request = async (method, path, payload, query, headers = null) => {
        const formattedUrl = generateUrl(path, query);
        // // Basic headers
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        axios.defaults.headers.common['Content-Type'] = 'application/json; charset=utf-8';
        axios.defaults.headers.common['Client-Time'] = new Date();
        axios.defaults.headers.common['X-Timezone'] = timezone;
        axios.defaults.headers.common.Locale = localStorage.getItem('local');
        // Auth headers
        axios.defaults.headers.common['Init-Id'] = localStorage.getItem('init-id');
        axios.defaults.headers.common.Authorization = localStorage.getItem('token');

        // Set custom headers
        if (headers) {
            Object.keys(headers).forEach((key) => {
                axios.defaults.headers.common[key] = headers[key];
            });
        }

        try {
            const { data } = await axios({ method, url: formattedUrl, data: payload });
            // console.log('data', data);
            if (data.status !== 0) {
                return {
                    error: true,
                    message: data.title || 'Something went wrong',
                    referenceName: data.referenceName || '',
                    data: null
                };
            }
            return {
                error: false,
                message: data.title || 'Success',
                data: data.data,
                referenceName: data.referenceName
            };
        } catch (error) {
            const res = error?.response?.data;
            return {
                error: true,
                message: res?.title || 'Something went wrong',
                data: null,
                referenceName: res?.referenceName || ''
            };
        }
    };

    return {
        /** @type {(path:string, query?:QueryParams, headers?:Headers)=>Promise<TApiResponse<any>>} */
        get: (path, query, headers) => request('get', path, null, query, headers),
        /** @type {<T>(path:string, payload:any, query?:QueryParams, headers?:Headers)=>Promise<TApiResponse<T>>} */
        post: (path, payload, query, headers) => request('post', path, payload, query, headers),
        /** @type {<T>(path:string, payload:any, query?:QueryParams, headers?:Headers)=>Promise<TApiResponse<T>>} */
        put: (path, payload, query, headers) => request('put', path, payload, query, headers),
        /** @type {<T>(path:string, payload?:any, query?:QueryParams, headers?:Headers)=>Promise<TApiResponse<T>>} */
        remove: (path, payload, query, headers) => request('delete', path, payload, query, headers)
    };
}
export default useApi;
